import React from 'react';

import {emptyObject} from '@packages/utilities/fp/utils';
import {
  useTransitionStyles,
  FloatingOverlay,
  FloatingNode,
} from '@floating-ui/react';

import {OverlayPortal} from '../../OverlayPortal';

import {useModal} from '../ModalProvider';
import {MODAL_ANIMATION_TIMING, INITIAL_OPACITY} from '../constants';
import {BackdropView} from './ModalBackdrop.styles';

import type {HTMLAttributes, SpringTooling} from '../../__SharedTypes';

export type ModalBackdropProps = {
  /**
   * Renders the contents.
   */
  children?: React.ReactNode;
  /**
   * When true, the modal fills the viewport. Used to enforce scroll lock and focus trap.
   */
  isFullScreen?: boolean;
  /**
   * Determines if the Modal should animate on open and close.
   */
  shouldDisableAnimations?: boolean;
  /**
   * Determines if the backdrop should be visible. Even if the backdrop is invisible, the Modal will still
   * close when the background is clicked. To adjust this behavior, considering using `shouldHideOnClickOutside`.
   */
  shouldShowBackdrop?: boolean;
} & HTMLAttributes &
  SpringTooling;

export const ModalBackdrop = React.forwardRef<
  HTMLDivElement,
  ModalBackdropProps
>(
  (
    {
      children,
      isFullScreen,
      shouldDisableAnimations,
      shouldShowBackdrop = true,
      style: incomingStyle,
      ...props
    },
    ref
  ) => {
    const {dialog} = useModal();
    const {nodeId} = dialog;
    const {isNonModalDialog, scrollBehavior, onCloseFocusRef} = useModal();

    const {isMounted, styles: transitionStyles} = useTransitionStyles(
      dialog.context,
      {
        duration: shouldDisableAnimations ? 0 : MODAL_ANIMATION_TIMING,
        initial: {
          opacity: shouldDisableAnimations ? 1 : INITIAL_OPACITY,
        },
        open: {
          opacity: 1,
        },
      }
    );

    /** Imperatively returns focus to ref provided in Modal provider */
    React.useEffect(() => {
      const closeFocusRef = onCloseFocusRef?.current;

      return () => {
        if (closeFocusRef && !isMounted) {
          closeFocusRef.focus();
        }
      };
    }, [isMounted, onCloseFocusRef]);

    if (!isMounted) return null;

    const showBackdrop = isNonModalDialog ? false : shouldShowBackdrop;

    const style = shouldDisableAnimations
      ? {...incomingStyle, opacity: 1}
      : {
          ...incomingStyle,
          ...transitionStyles,
        };

    return (
      <FloatingNode id={nodeId}>
        <OverlayPortal>
          {/* Mask should theoretically be able to replace both of FloatingOverlay & BackdropView, I believe  - https://webflow.atlassian.net/browse/DS-555 */}
          <FloatingOverlay
            lockScroll={!isNonModalDialog || isFullScreen}
            style={isNonModalDialog ? {pointerEvents: 'none'} : emptyObject}
          >
            <BackdropView
              data-modal-dialog
              ref={ref}
              scrollBehavior={scrollBehavior}
              shouldShowBackdrop={showBackdrop}
              style={style}
              {...props}
            >
              {children}
            </BackdropView>
          </FloatingOverlay>
        </OverlayPortal>
      </FloatingNode>
    );
  }
);

ModalBackdrop.displayName = 'ModalBackdrop';
